import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Loader2, Send, ImagePlus, X, Save } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { TiptapEditor } from "@/components/editor/TiptapEditor"
import { useAuth } from "@/context/AuthContext"
import { useToast } from "@/hooks/useToast"
import { createPost, getCategories, getUniqueSlug, isNetworkFetchError } from "@/lib/db"
import { uploadImage } from "@/lib/storage"
import { postSchema, type PostFormData } from "@/lib/validators"
import { generateSlug, calculateReadingTime, generateExcerpt } from "@/lib/utils"
import type { Category } from "@/types"

export default function WritePage() {
  const navigate = useNavigate()
  const { user, firebaseUser } = useAuth()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [content, setContent] = useState("")
  const [coverImage, setCoverImage] = useState<string | null>(null)
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [categories, setCategories] = useState<Category[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<PostFormData>({
    resolver: zodResolver(postSchema),
    defaultValues: {
      title: "",
      content: "",
      tags: [],
      category: "",
    },
  })

  useEffect(() => {
    async function loadCategories() {
      try {
        const cats = await getCategories()
        setCategories(cats)
      } catch (error) {
        if (!isNetworkFetchError(error)) {
          console.error("Failed to load categories:", error)
        }
      }
    }

    loadCategories()
  }, [])

  // Sync content with react-hook-form
  useEffect(() => {
    setValue("content", content)
  }, [content, setValue])

  // Sync tags with react-hook-form
  useEffect(() => {
    setValue("tags", tags)
  }, [tags, setValue])

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      const tag = tagInput
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "")
      if (tag && !tags.includes(tag) && tags.length < 5) {
        setTags([...tags, tag])
        setTagInput("")
      }
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove))
  }

  const handleCoverImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const authorId = user?.uid || firebaseUser?.id
    const file = e.target.files?.[0]
    if (!file || !authorId) return

    try {
      const url = await uploadImage(file, `covers/${authorId}`)
      setCoverImage(url)
      toast({ title: "Cover image uploaded" })
    } catch (error) {
      toast({ title: "Failed to upload image", variant: "destructive" })
    }
  }

  const handleSaveDraft = async () => {
    const authorId = user?.uid || firebaseUser?.id
    if (!authorId) {
      toast({ title: "Please sign in to save drafts", variant: "destructive" })
      return
    }

    // Get current form values
    const title = watch("title")
    const subtitle = watch("subtitle")
    const category = watch("category")

    if (!title || !content) {
      toast({ title: "Title and content are required", variant: "destructive" })
      return
    }

    setIsSavingDraft(true)
    try {
      // Add timestamp to slug for drafts to avoid conflicts
      const baseSlug = generateSlug(title)
      const slug = `${baseSlug}-draft-${Date.now()}`
      const readingTime = calculateReadingTime(content)
      const excerpt = generateExcerpt(content)

      await createPost({
        title,
        subtitle: subtitle || "",
        authorId,
        slug,
        content,
        excerpt,
        coverImage: coverImage || "",
        tags,
        category: category || "",
        status: "draft",
        readingTime,
        viewCount: 0,
        upvoteCount: 0,
        downvoteCount: 0,
        commentCount: 0,
        shareCount: 0,
        publishedAt: null,
      })

      toast({ title: "Draft saved!" })
      navigate("/drafts")
    } catch (error) {
      if (!isNetworkFetchError(error)) {
        console.error("Failed to save draft:", error)
      }
      toast({
        title: "Failed to save draft",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      })
    } finally {
      setIsSavingDraft(false)
    }
  }

  const onSubmit = async (data: PostFormData) => {
    const authorId = user?.uid || firebaseUser?.id
    if (!authorId) {
      toast({ title: "Please sign in to publish", variant: "destructive" })
      return
    }

    setIsSubmitting(true)
    try {
      const slug = await getUniqueSlug(generateSlug(data.title))
      const readingTime = calculateReadingTime(content)
      const excerpt = generateExcerpt(content)

      await createPost({
        title: data.title,
        subtitle: data.subtitle || "",
        authorId,
        slug,
        content,
        excerpt,
        coverImage: coverImage || "",
        tags,
        category: data.category,
        status: "published",
        readingTime,
        viewCount: 0,
        upvoteCount: 0,
        downvoteCount: 0,
        commentCount: 0,
        shareCount: 0,
        publishedAt: new Date().toISOString(),
      })

      toast({ title: "Post published!" })
      navigate(`/post/${slug}`)
    } catch (error) {
      toast({ title: "Failed to save post", variant: "destructive" })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Cover Image */}
        <div className="relative">
          {coverImage ? (
            <div className="relative aspect-video w-full overflow-hidden rounded-lg">
              <img src={coverImage} alt="Cover" className="h-full w-full object-cover" />
              <Button type="button" variant="destructive" size="icon" className="absolute right-2 top-2" onClick={() => setCoverImage(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <label className="flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/50 hover:bg-muted">
              <ImagePlus className="mb-2 h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Add a cover image</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleCoverImageUpload} />
            </label>
          )}
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Input
            {...register("title")}
            placeholder="Article title..."
            className="border-0 text-3xl font-bold placeholder:text-muted-foreground focus-visible:ring-0"
          />
          {errors.title && <p className="text-sm text-red-500">{errors.title.message}</p>}
        </div>

        {/* Subtitle */}
        <Input
          {...register("subtitle")}
          placeholder="Add a subtitle (optional)"
          className="border-0 text-xl text-muted-foreground placeholder:text-muted-foreground focus-visible:ring-0"
        />

        {/* Editor */}
        <TiptapEditor content={content} onChange={setContent} />

        {/* Tags */}
        <div className="space-y-2">
          <Label>Tags (up to 5)</Label>
          <div className="flex flex-wrap gap-2">
            {tags.map(tag => (
              <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                #{tag} <X className="ml-1 h-3 w-3" />
              </Badge>
            ))}
            {tags.length < 5 && (
              <Input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Add tag..."
                className="w-32"
              />
            )}
          </div>
        </div>

        {/* Category & Actions */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select onValueChange={value => setValue("category", value)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={isSavingDraft || isSubmitting}>
              {isSavingDraft ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Draft
            </Button>
            <Button type="submit" disabled={isSubmitting || isSavingDraft}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Publish
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
